import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Briefcase, FileText, Users, Edit, Lock, Unlock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { adminApi } from '@/services/adminApi';

/**
 * AdminPositionDetailPage — single jobPosition deep-dive for BO-05.
 *
 * Lets the admin edit title/description, close/reopen the position
 * (which cascades to its postings), or soft-delete it. Shows links
 * to the related postings + applications cross-tenant filters.
 */
export default function AdminPositionDetailPage() {
  const { companyId = '', posId = '' } = useParams<{ companyId: string; posId: string }>();
  type Detail = Awaited<ReturnType<typeof adminApi.positions.detail>>;
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const [editOpen, setEditOpen] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);
  const [pendingReopen, setPendingReopen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const detail = await adminApi.positions.detail(companyId, posId);
        if (!cancelled) setData(detail);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, posId, refreshTick]);

  const refresh = () => setRefreshTick((t) => t + 1);

  if (error) return <div className="text-sm text-destructive">{error}</div>;
  if (!data) return <div className="text-sm text-muted-foreground">Loading...</div>;

  const isActive = data.status !== 'closed';

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="gap-1.5">
          <Link to={`/admin/customers/${companyId}`}><ArrowLeft className="h-4 w-4" /> Back to customer</Link>
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            {data.title || '(untitled position)'}
            <Badge variant={isActive ? 'default' : 'outline'}>{data.status ?? 'unknown'}</Badge>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            <Link to={`/admin/customers/${companyId}`} className="hover:underline">
              {data.companyName || companyId}
            </Link>
          </p>
          <p className="text-xs text-muted-foreground font-mono mt-1">{posId}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditOpen(true)}>
            <Edit className="h-3.5 w-3.5" /> Edit
          </Button>
          {isActive ? (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setPendingClose(true)}>
              <Lock className="h-3.5 w-3.5" /> Close
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setPendingReopen(true)}>
              <Unlock className="h-3.5 w-3.5" /> Reopen
            </Button>
          )}
          <Button size="sm" variant="outline" className="gap-1.5 text-destructive border-destructive/40 hover:bg-destructive/10" onClick={() => setPendingDelete(true)}>
            <Trash2 className="h-3.5 w-3.5" /> Soft delete
          </Button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-3 gap-3">
        <KpiCard label="Postings" value={data.kpi.postingsCount} icon={<Briefcase className="h-4 w-4" />} href={`/admin/postings?companyId=${companyId}`} />
        <KpiCard label="Applications" value={data.kpi.applicationsCount} icon={<FileText className="h-4 w-4" />} href={`/admin/applications?companyId=${companyId}`} />
        <KpiCard label="Members" value={data.kpi.membersCount} icon={<Users className="h-4 w-4" />} />
      </div>

      {/* Description */}
      {data.description && (
        <Card className="border-border/50">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">Description</h2>
            <p className="text-sm whitespace-pre-wrap">{data.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Raw doc */}
      <Card className="border-border/50">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-border/50">
            <h2 className="text-sm font-semibold">Raw position document</h2>
          </div>
          <pre className="p-5 text-xs overflow-x-auto bg-muted/30 max-h-[400px]">
            {JSON.stringify(data.raw, null, 2)}
          </pre>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <EditPositionDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        title={data.title ?? ''}
        description={data.description ?? ''}
        onSaved={async (payload) => {
          await adminApi.positions.update(companyId, posId, payload);
          toast.success('Position updated');
          setEditOpen(false);
          refresh();
        }}
      />
      <ReasonConfirmDialog
        open={pendingClose}
        onOpenChange={setPendingClose}
        title="Close position?"
        description="The position is marked closed. Postings under it become inactive."
        confirmLabel="Close"
        destructive
        onConfirm={async (reason) => {
          await adminApi.positions.setStatus(companyId, posId, 'closed', reason);
          toast.success('Position closed');
          refresh();
        }}
      />
      <ReasonConfirmDialog
        open={pendingReopen}
        onOpenChange={setPendingReopen}
        title="Reopen position?"
        description="The position becomes active again."
        confirmLabel="Reopen"
        onConfirm={async (reason) => {
          await adminApi.positions.setStatus(companyId, posId, 'active', reason);
          toast.success('Position reopened');
          refresh();
        }}
      />
      <ReasonConfirmDialog
        open={pendingDelete}
        onOpenChange={setPendingDelete}
        title="Soft delete position?"
        description="The position is hidden from the recruiter UI. Postings + applications under it become unreachable from the company workspace but data is preserved."
        confirmLabel="Soft delete"
        destructive
        onConfirm={async (reason) => {
          await adminApi.positions.softDelete(companyId, posId, reason);
          toast.success('Position deleted');
          window.location.href = `/admin/customers/${companyId}`;
        }}
      />
    </div>
  );
}

function KpiCard({ label, value, icon, href }: { label: string; value: number; icon: React.ReactNode; href?: string }) {
  const inner = (
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-1 text-xl font-semibold tabular-nums">{value.toLocaleString('en-US')}</p>
      </div>
      <span className="text-muted-foreground">{icon}</span>
    </CardContent>
  );
  if (href) return <Card className="border-border/50 hover:border-primary/40 transition-colors"><Link to={href} className="block">{inner}</Link></Card>;
  return <Card className="border-border/50">{inner}</Card>;
}

function EditPositionDialog({
  open, onOpenChange, title: initialTitle, description: initialDescription, onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description: string;
  onSaved: (payload: Record<string, unknown> & { reason: string }) => Promise<void>;
}) {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(initialTitle);
      setDescription(initialDescription);
      setReason('');
    }
  }, [open, initialTitle, initialDescription]);

  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    try {
      const payload: Record<string, unknown> & { reason: string } = { reason };
      if (title !== initialTitle) payload['title'] = title;
      if (description !== initialDescription) payload['description'] = description;
      const keys = Object.keys(payload).filter((k) => k !== 'reason');
      if (keys.length === 0) {
        toast.info('No changes to save.');
        return;
      }
      await onSaved(payload);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit position</DialogTitle>
          <DialogDescription>Updating title + description.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="pos-title">Title</Label>
            <Input id="pos-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pos-description">Description</Label>
            <Textarea id="pos-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={6} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pos-reason">Reason <span className="text-destructive">*</span></Label>
            <Textarea id="pos-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !reason.trim()}>{busy ? 'Saving...' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReasonConfirmDialog({
  open, onOpenChange, title, description, confirmLabel, destructive, onConfirm,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);
  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="pos-reason-confirm">Reason <span className="text-destructive">*</span></Label>
          <Textarea id="pos-reason-confirm" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); submit(); }}
            disabled={busy || !reason.trim()}
            className={destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
          >
            {busy ? 'Working...' : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
